import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { configureAuth } from './services/auth.ts';
import theme from './theme.ts';
import AuthController from './components/auth/AuthController.tsx';
import ProblemWorkspace from './pages/ProblemWorkspace/index.tsx';
import ProposalDetail from './pages/ProposalDetail/index.tsx';
import SolutionDetail from './pages/SolutionDetail/index.tsx';
import PageLayout from './components/PageLayout.tsx';
import Rain from './components/Rain.tsx';
import ContributorDashboard from './components/ContributorDashboard.tsx';
import MarketplaceHome from './components/MarketPlaceHome.tsx';
import ProposalSubmission from './components/ProposalSubmission.tsx';
import AvatarTest from './components/AvatarTest.tsx';
import Landing from './pages/Landing/index.tsx';
import ExecutivePage from './pages/Executive/index.tsx';
import Dashboard from './pages/Dashboard/index.tsx';
import Analytics from './pages/Analytics/index.tsx';
import Optimization from './pages/Optimization/index.tsx';
import Proposals from './pages/Proposals/index.tsx';
import Settings from './pages/Settings/index.tsx';
import Solutions from './pages/Solutions/index.tsx';
import Attribution from './pages/Attribution/index.tsx';
import Home from './pages/Home/index.tsx';

function App() {
  console.log('App component rendering - initial load');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [debugInfo, setDebugInfo] = useState('Initializing app...');
  
  // Load AWS configuration on app startup
  useEffect(() => {
    console.log('App useEffect - loading config');
    setDebugInfo('Loading configuration...');
    
    const loadConfig = async () => {
      try {
        // Fetch the AWS configuration
        const response = await fetch('/aws-config.json');
        if (!response.ok) {
          throw new Error('Failed to load AWS configuration');
        }
        
        const config = await response.json();
        console.log('Configuration loaded successfully');
        setDebugInfo('Config loaded, setting up auth...');
        
        // Configure authentication with the loaded settings
        configureAuth({
          region: config.region,
          userPoolId: config.userPoolId,
          userPoolWebClientId: config.userPoolWebClientId
        });
        
        setIsLoadingConfig(false);
      } catch (error) {
        console.error('Error loading AWS configuration:', error);
        setDebugInfo('Error loading config: ' + (error instanceof Error ? error.message : String(error)));
        
        // In development, we can continue without auth config
        if (process.env.NODE_ENV === 'development') {
          console.log('Development mode: auth configuration bypass');
          setIsLoadingConfig(false);
          // Note: We're not automatically setting isAuthenticated=true anymore
          // This requires explicit login even in development
        }
      }
    };
    
    loadConfig();
  }, []);
  
  // Handle successful authentication
  const handleAuthenticated = () => {
    console.log('User authenticated successfully');
    setDebugInfo('Authentication successful, loading app...');
    setIsAuthenticated(true);
  };
  
  // Special debug component for production troubleshooting
  const DevDebugPanel = () => (
    <Box sx={{ 
      position: 'fixed', 
      bottom: 0, 
      left: 0, 
      right: 0, 
      p: 2, 
      backgroundColor: 'rgba(0,0,0,0.8)', 
      color: 'white',
      zIndex: 9999,
      textAlign: 'center'
    }}>
      <Typography variant="body2">
        App State: {isLoadingConfig ? "Loading Config" : (isAuthenticated ? "Authenticated" : "Not Authenticated")}
      </Typography>
      <Typography variant="body2">
        Debug Info: {debugInfo}
      </Typography>
      <Typography variant="body2">
        Environment: {process.env.NODE_ENV || 'unknown'}
      </Typography>
      {!isAuthenticated && !isLoadingConfig && (
        <Box sx={{ mt: 1 }}>
          <button onClick={() => {
            console.log('Debug authentication bypass');
            handleAuthenticated();
          }} style={{ padding: '8px 16px', cursor: 'pointer' }}>
            Debug Login
          </button>
        </Box>
      )}
    </Box>
  );
  
  // If still loading config, show minimal loading UI
  if (isLoadingConfig) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
      }}>
        <Typography variant="h4" sx={{ mb: 2 }}>Loading Problem Source...</Typography>
        <Typography variant="body1">{debugInfo}</Typography>
        <DevDebugPanel />
      </Box>
    );
  }
  
  console.log('Rendering main app UI, auth state:', isAuthenticated);
  
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ position: 'relative', minHeight: '100vh' }}>
          <Rain />
          <PageLayout>
            <main className="app">
              {!isAuthenticated ? (
                // Show auth screen if not authenticated
                <>
                  <AuthController onAuthenticated={handleAuthenticated} />
                  <DevDebugPanel />
                </>
              ) : (
                // Show main app if authenticated
                <>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/optimization" element={<Optimization />} />
                    <Route path="/proposals" element={<Proposals />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/contributor-dashboard" element={<ContributorDashboard />} />
                    <Route path="/marketplace" element={<MarketplaceHome />} />
                    <Route path="/submit-proposal" element={<ProposalSubmission />} />
                    <Route path="/proposal-detail/:id" element={<ProposalDetail />} />
                    <Route path="/executive/:id" element={<ExecutivePage />} />
                    <Route path="/solutions" element={<Solutions />} />
                    <Route path="/solution-detail/:id" element={<SolutionDetail />} />
                    <Route path="/avatar-test" element={<AvatarTest />} />
                    <Route path="/attribution" element={<Attribution />} />
                    <Route path="/problem-workspace" element={<ProblemWorkspace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  <DevDebugPanel />
                </>
              )}
            </main>
          </PageLayout>
          
          <div className="ai-assistant" id="aiAssistant">
            <div className="voice-wave"></div>
            <div className="assistant-message"></div>
          </div>
          
          <div className="notification-layer" id="notifications"></div>
        </Box>
      </ThemeProvider>
    </Router>
  );
}

export default App; 